import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

// Schema for a workspace receipt sender
export const workspaceReceiptSenderSchema = z
    .object({
        id: z.string(),
        email: z.string().email().nullable(),
        phoneNumber: z.string().nullable(),
        workspaceId: z.string().uuid(),
        limit: z.number().int().positive(),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
    })
    .refine(
        (data) => (data.email !== null) !== (data.phoneNumber !== null),
        "Exactly one of email or phoneNumber must be provided",
    );

export type WorkspaceReceiptSender = z.infer<
    typeof workspaceReceiptSenderSchema
>;

// Request body for creating a receipt sender
export const createWorkspaceReceiptSenderEmailSchema = z.object({
    email: z.string().email().trim(),
    verificationCode: z.string().min(1, "Verification code is required"),
});

export type CreateWorkspaceReceiptSenderEmailRequest = z.infer<
    typeof createWorkspaceReceiptSenderEmailSchema
>;

export const createWorkspaceReceiptSenderPhoneNumberSchema = z.object({
    phoneNumber: z.string().trim(),
    verificationCode: z.string().min(1, "Verification code is required"),
});

export type CreateWorkspaceReceiptSenderPhoneNumberRequest = z.infer<
    typeof createWorkspaceReceiptSenderPhoneNumberSchema
>;

// Response for listing receipt senders
export const listWorkspaceReceiptSendersResponseSchema = z.object({
    receiptSenders: z.array(workspaceReceiptSenderSchema),
});

export type ListWorkspaceReceiptSendersResponse = z.infer<
    typeof listWorkspaceReceiptSendersResponseSchema
>;

export const workspaceReceiptSenderContract = c.router(
    {
        getForWorkspace: {
            method: "GET",
            path: "/",
            responses: {
                200: listWorkspaceReceiptSendersResponseSchema,
            },
        },
        createEmail: {
            method: "POST",
            path: "/email",
            body: createWorkspaceReceiptSenderEmailSchema,
            responses: {
                201: workspaceReceiptSenderSchema,
                400: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        createPhoneNumber: {
            method: "POST",
            path: "/phone-number",
            body: createWorkspaceReceiptSenderPhoneNumberSchema,
            responses: {
                201: workspaceReceiptSenderSchema,
                400: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        remove: {
            method: "DELETE",
            path: "/:id",
            body: z.object({}),
            responses: {
                200: z.object({}),
                404: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/workspace-receipt-senders/:workspaceId",
    },
);
